import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, ChevronDown, Loader2, Search, X } from 'lucide-react';
import { useAuth, API } from '../../App';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';

/**
 * Inline vehicle picker shown directly in the rides table.
 * Click the cell -> popover with searchable vehicle list -> pick to assign.
 *
 * Props:
 *  - ride: ride object (must contain id, vehicle_id, vehicle_plate)
 *  - vehicles: [{id, plate_number, vehicle_type?, model?}]
 *  - onUpdated(updatedRide)
 */
export default function InlineVehiclePicker({ ride, vehicles, onUpdated }) {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return vehicles;
    return vehicles.filter((v) =>
      [v.plate_number, v.vehicle_type, v.model]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q)),
    );
  }, [vehicles, query]);

  const assign = async (vehicleId) => {
    if (busy) return;
    if (vehicleId === (ride.vehicle_id || null)) {
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      const res = await axios.put(
        `${API}/rides/${ride.id}`,
        { vehicle_id: vehicleId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updated = res.data;
      if (onUpdated) onUpdated(updated);
      const plate = vehicleId
        ? vehicles.find((v) => v.id === vehicleId)?.plate_number || 'vehicle'
        : null;
      toast.success(vehicleId ? `Vehicle assigned: ${plate}` : 'Vehicle unassigned');
      setOpen(false);
      setQuery('');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to update vehicle';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="group/vp inline-flex items-center gap-1.5 px-2 py-1 -mx-2 -my-1 rounded hover:bg-slate-100 transition-colors text-sm max-w-full"
          data-testid={`inline-vehicle-picker-${ride.id}`}
          title="Click to assign vehicle"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-500 flex-shrink-0" /> : null}
          <span className={`truncate ${ride.vehicle_plate ? 'font-medium text-sky-700' : 'text-slate-400 italic'}`}>
            {ride.vehicle_plate || 'N/A'}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-slate-400 flex-shrink-0 opacity-0 group-hover/vp:opacity-100 transition-opacity" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-0"
        align="start"
        onClick={(e) => e.stopPropagation()}
        data-testid={`inline-vehicle-picker-popover-${ride.id}`}
      >
        <div className="p-2 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              autoFocus
              placeholder="Search plate or type…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-7 h-8 text-sm"
              data-testid={`inline-vehicle-picker-search-${ride.id}`}
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          <button
            type="button"
            onClick={() => assign(null)}
            disabled={busy}
            className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-100 text-left ${
              !ride.vehicle_id ? 'bg-slate-50 font-medium' : ''
            }`}
            data-testid={`inline-vehicle-picker-unassign-${ride.id}`}
          >
            <X className="h-3.5 w-3.5 text-slate-400" />
            <span className="text-slate-600 italic">No vehicle</span>
            {!ride.vehicle_id && <Check className="h-3.5 w-3.5 text-emerald-600 ml-auto" />}
          </button>
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-xs text-slate-400 text-center">No vehicles found</div>
          ) : (
            filtered.map((v) => {
              const isCurrent = v.id === ride.vehicle_id;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => assign(v.id)}
                  disabled={busy}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-100 text-left ${
                    isCurrent ? 'bg-slate-50 font-medium' : ''
                  }`}
                  data-testid={`inline-vehicle-picker-option-${ride.id}-${v.id}`}
                >
                  <span className="font-medium text-sky-700">{v.plate_number}</span>
                  {(v.vehicle_type || v.model) && (
                    <span className="text-xs text-slate-500 truncate">
                      {[v.model, v.vehicle_type].filter(Boolean).join(' · ')}
                    </span>
                  )}
                  {isCurrent && <Check className="h-3.5 w-3.5 text-emerald-600 ml-auto" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
